import {
  addMonths,
  differenceInMonths,
  endOfMonth,
  isAfter,
  isBefore,
  startOfMonth,
} from "date-fns";
import {
  currencyDecimalPlaces,
  milesToKmConversion,
} from "@s2z-platform/constants";
import type {
  SubscriptionType,
  SupportedCurrencyCode,
  AccountType,
} from "@s2z-platform/constants";
import type {
  DateRange,
  DisplayUnitsNumber,
  ISO8601DateString,
  WorkingUnitsNumber,
  YYYYMMDDDateString,
  YYYYMMDateString,
  HexColour,
} from "@s2z-platform/types";

// BANANA: EPIC CW TIMES RESULTED IN THIS ESLINT-DISABLE BEING ADDED.
// IT APPLIES TO EVERYTHING BELOW, AND I DON'T HAVE TIME
// TO FIX THE CONSEQUENCES RIGHT NOW.
/* eslint-disable */
/**
 * Update object with given data
 */
export function updateObject(object: any, data: any, skipUndefined = true) {
  if (!object || !data) {
    return object;
  }

  if (
    typeof object !== "object" ||
    typeof data !== "object" ||
    Object.keys(data).length === 0
  ) {
    return object;
  }

  for (const key of Object.keys(data)) {
    // Skip id
    if (key !== "id") {
      if (!skipUndefined) {
        object[key] = data[key];
      } else {
        // Apply only if not undefined
        if (data[key] !== undefined) {
          object[key] = data[key];
        }
      }
    }
  }

  return object;
}

/**
 * Needed because Stripe doesn't offer an amountForDisplay or any distinguisher between 0 and 2 decimal placed currencies <_< n1 Stripe!
 * @param currency 3 letter currency code (case doesn't matter)
 * @param amount cost in smallest denomination
 * @returns displayable cost eg: ("GBP", 2850) => 28.50
 */
export function workingToDisplayUnits(currency: string, amount: number) {
  const decimalDigits = currency
    ? currencyDecimalPlaces[currency.toUpperCase() as SupportedCurrencyCode]
    : 2;
  return Number(
    (amount / 10 ** decimalDigits).toFixed(decimalDigits),
  ) as DisplayUnitsNumber;
}

/**
 * Returns working units as a string, with any appropriate trailing '0's, for direct display to the user
 */
export function workingToDisplayUnitsAsString(
  currency: string,
  amount: number,
) {
  const decimalDigits =
    currencyDecimalPlaces[currency.toUpperCase() as SupportedCurrencyCode];
  return (amount / 10 ** decimalDigits).toFixed(decimalDigits);
}

export function displayToWorkingUnits(currency: string, amount: number) {
  const decimalDigits = currency
    ? currencyDecimalPlaces[currency.toUpperCase() as SupportedCurrencyCode]
    : 2;
  return Number(
    (amount * 10 ** decimalDigits).toFixed(0),
  ) as WorkingUnitsNumber;
}

/**
 * Convert Stripe timestamp into Date
 *
 * Note: Stripe timestamps are in seconds
 */
export function stripeTimestampToDate(timestamp: number): Date {
  return new Date(timestamp * 1000);
}

export function dateToStripeTimestamp(date: Date): number {
  return Math.floor(date.getTime() / 1000);
}

/** Optional leading +, digits, and spaces (no leading/trailing spaces) */
export const MobileNumberRegex = /^\+?\d[\d ]*\d$/;

/** Alphanumeric characters, hyphens, apostrophes and periods only */
export const PersonNameRegex = /^[a-zA-Z0-9\-.']+$/;

/** Alphanumeric characters, spaces, hyphens, periods, ampersands, apostrophes, and commas */
export const CompanyNameRegex = /^[a-zA-Z0-9 ,.\-\/'#&]+$/;

/** Free text: allows letters, numbers, common punctuation, whitespace, and Unicode — rejects HTML tags */
export const FreeTextRegex = /^[^<>]*$/;

/** Address fields: allows letters, numbers, spaces, commas, hyphens, periods, slashes, hash, apostrophes, and ampersands */
export const AddressFieldRegex = /^[a-zA-Z0-9 ,.\-\/'#&]+$/;

/** General structured fields: allows letters, numbers, spaces, commas, hyphens, periods, underscores, and hash */
export const GeneralFieldRegex = /^[a-zA-Z0-9 \-._#,₀-₉]+$/;

/** Vehicle reference data fields: broader set for DVLA/EPA data — adds parentheses, slashes, and ampersands */
export const VehicleDataRegex = /^[a-zA-Z0-9 \-._#()/&]+$/;

/** Validation messages */
export const FREE_TEXT_VALIDATION_MESSAGE =
  "The characters < and > are not allowed";
export const ADDRESS_FIELD_VALIDATION_MESSAGE =
  "Only letters, numbers, spaces, commas, hyphens, periods, slashes, hash, apostrophes, and ampersands are allowed";
export const GENERAL_FIELD_VALIDATION_MESSAGE =
  "Only letters, numbers, spaces, commas, hyphens, periods, underscores, and hash are allowed";
export const PERSON_NAME_VALIDATION_MESSAGE =
  "Only letters, numbers, hyphens, apostrophes and periods are allowed";
export const COMPANY_NAME_VALIDATION_MESSAGE =
  "Only letters, numbers, spaces, commas, hyphens, periods, slashes, hash, apostrophes, and ampersands are allowed";

/** Looks like 2023-08-18 */
export const YYYYMMDDDateRegex = /^\d\d\d\d-\d\d-\d\d$/;

/** Looks like 2023-08 */
export const YYYYMMDateRegex = /^\d\d\d\d-\d\d$/;

export function getImpactPeriodForCreateDate(date: Date): DateRange {
  return {
    start: startOfMonth(date),
    end: endOfMonth(date),
  };
}

export type UntypedMetadata = { [key: string]: string };

export function getFootprintPercentage(
  subscriptionType: SubscriptionType,
  userProfileType: AccountType,
) {
  if (subscriptionType === "platform" && userProfileType === "business") {
    // If the subscription is earth-positive workforce, it only contributes 20%
    // of its impact to the business' footprint
    return 20;
  }

  // Otherwise, apply all impact
  return 100;
}

/**
 * Returns the number of months (inclusive) between two dates.
 *
 * For example, periodLength(Jan 2023, Mar 2023) = 3.
 */
export function periodLength(start: Date, end: Date) {
  return differenceInMonths(
    startOfMonth(addMonths(end, 1)),
    startOfMonth(start),
  );
}

export function isBetween(
  date: Date,
  start: Date,
  end: Date,
  inclusivity: "inclusive" | "exlusive",
) {
  if (inclusivity === "inclusive") {
    return !isBefore(date, start) && !isAfter(date, end);
  }
  return isAfter(date, start) && isBefore(date, end);
}

export function getProportionOfImpactWithinPeriod(
  impactDateRange: { start: Date; end: Date },
  dateRangeToCompare: { start: Date; end: Date },
  includeUnassigned?: boolean,
) {
  // If all of impact is within specified period, return 1
  if (
    (isBetween(
      impactDateRange.start,
      dateRangeToCompare.start,
      dateRangeToCompare.end,
      "inclusive",
    ) &&
      isBetween(
        impactDateRange.end,
        dateRangeToCompare.start,
        dateRangeToCompare.end,
        "inclusive",
      )) ||
    (!impactDateRange.start && !impactDateRange.end && includeUnassigned)
  ) {
    return { proportion: 1 };
  } else if (
    // If none of specified period is within impact range, return 0
    !isBetween(
      dateRangeToCompare.start,
      impactDateRange.start,
      impactDateRange.end,
      "inclusive",
    ) &&
    !isBetween(
      dateRangeToCompare.end,
      impactDateRange.start,
      impactDateRange.end,
      "inclusive",
    )
  ) {
    return { proportion: 0 };
  } else {
    // If some of specified period is within impact range, calculate proportion
    const numberOfMonthsAtStart = differenceInMonths(
      dateRangeToCompare.start,
      impactDateRange.start,
    );
    const numberOfMonthsAtEnd = differenceInMonths(
      impactDateRange.end,
      dateRangeToCompare.end,
    );
    const numberOfMonthsImpact =
      differenceInMonths(impactDateRange.end, impactDateRange.start) + 1;

    let numberOfMonthsWithinDateRange = 0;

    if (numberOfMonthsAtStart > 0 && numberOfMonthsAtEnd <= 0) {
      console.debug(`Impact ends within date range but starts outside of it`);
      numberOfMonthsWithinDateRange =
        numberOfMonthsImpact - numberOfMonthsAtStart;
    } else if (numberOfMonthsAtEnd > 0 && numberOfMonthsAtStart <= 0) {
      console.debug(`Impact starts within date range but ends outside of it`);
      numberOfMonthsWithinDateRange =
        numberOfMonthsImpact - numberOfMonthsAtEnd;
    } else {
      console.debug(`Impact starts and ends outside of date range`);
      numberOfMonthsWithinDateRange =
        numberOfMonthsImpact - numberOfMonthsAtStart - numberOfMonthsAtEnd;
    }

    return {
      proportion: numberOfMonthsWithinDateRange / numberOfMonthsImpact,
    };
  }
}

/**
 * @param date Date object or ISO8601 date string
 * @param options utc = true for UTC, false for local time
 * @returns YYYY-MM string
 */
export const convertToYYYYMM = (
  date: Date | string,
  { utc = false }: { utc?: boolean } = {},
) => {
  const inputDate = typeof date === "string" ? new Date(date) : date;
  const month = (utc ? inputDate.getUTCMonth() : inputDate.getMonth()) + 1;
  const monthString = `${month < 10 ? 0 : ""}${month}`;
  const year = utc ? inputDate.getUTCFullYear() : inputDate.getFullYear();
  return `${year}-${monthString}` as YYYYMMDateString;
};

/**
 * @param date Date object or ISO8601 date string
 * @param options utc = true for UTC, false for local time
 * @returns YYYY-MM-DD string
 */
export const convertToYYYYMMDD = (
  date: Date | string,
  { utc = false }: { utc?: boolean } = {},
) => {
  const inputDate = typeof date === "string" ? new Date(date) : date;
  const day = utc ? inputDate.getUTCDate() : inputDate.getDate();
  const dayString = `${day < 10 ? 0 : ""}${day}`;
  return `${convertToYYYYMM(date, { utc })}-${dayString}` as YYYYMMDDDateString;
};

/**
 * Converts to Date object at start of month.
 */
export const convertYYYYMMToDate = (
  dateString: YYYYMMDDDateString | YYYYMMDateString,
) => {
  const parts = dateString.split("-").map(Number);

  if (parts.length === 2) {
    // YYYY-MM format
    const [year, month] = parts;
    return new Date(year, month - 1, 1);
  }
  if (parts.length === 3) {
    // YYYY-MM-DD format
    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  }
  throw new Error("Invalid date format. Expected YYYY-MM or YYYY-MM-DD");
};

export function milesToKm(miles: number) {
  return miles * milesToKmConversion;
}

/**
 *
 * @param input string to be capitalised
 * @param capitaliseAfter Array of string symbols to capitalise word after, default: undefined
 * @returns capitalised string or ""
 */
export function capitaliseFirstLetter(
  input: string,
  capitaliseAfter?: (" " | "-")[],
) {
  if (input && (!capitaliseAfter || capitaliseAfter.length < 1))
    return `${input[0].toUpperCase()}${input.slice(1).toLowerCase()}`;

  if (input && capitaliseAfter) {
    let res = input;
    capitaliseAfter.forEach((symbol) => {
      res = res
        .split(symbol)
        .map((string) => {
          if (!string[0]) return string;
          return `${string[0].toUpperCase()}${string.slice(1)}`;
        })
        .join(symbol);
    });
    return res;
  }
  return "";
}

export function genArrayOfYears(
  noOfYears: number,
  lastYear: number = new Date().getFullYear(),
) {
  const arr = [];
  for (let i = 0; i < noOfYears; i += 1) {
    arr.push(lastYear - i);
  }
  return arr;
}

export function genArrayOfYearsSince(
  startYear: number,
  lastYear: number = new Date().getFullYear(),
) {
  return Array.from(
    { length: lastYear - startYear + 1 },
    (_, i) => startYear + i,
  ).sort((a, b) => (a > b ? -1 : 1));
}

/**
 * @description Extracts domains from a string and formats them like "example.com" or "sub.example.com"
 * @example extractDomains("https://example.com/products?id=123") => ["example.com"]
 */
export function extractDomains(text?: string) {
  if (text === "*") return "*";
  const regex =
    /(?:https?:\/\/)?(?:www\.)?([a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,})(?:\/\S*)?/gi;

  const matches = (text || "").match(regex);
  return matches
    ? matches.map((match) =>
        match.replace(/^(?:https?:\/\/)?(?:www\.)?/, "").replace(/\/.*$/, ""),
      )[0]
    : "";
}

export function arbitrarySort<T>(array: T[], orderArray: T[]): T[] {
  return [...array].sort(
    (a, b) => orderArray.indexOf(a) - orderArray.indexOf(b),
  );
}

export type ImpactConfigModeType = "fixed" | "percentage";

export function roundToNearest(
  value: number,
  nearestMultipleOf: number,
  method: "round" | "ceil" | "floor" = "round",
) {
  switch (method) {
    case "round": {
      return Math.round(value / nearestMultipleOf) * nearestMultipleOf;
    }
    case "ceil": {
      return Math.ceil(value / nearestMultipleOf) * nearestMultipleOf;
    }
    case "floor": {
      return Math.floor(value / nearestMultipleOf) * nearestMultipleOf;
    }
  }
}

export function convertToISO8601(
  date: Date | YYYYMMDDDateString | YYYYMMDateString,
) {
  const inputDate = typeof date === "string" ? new Date(date) : date;
  return inputDate.toISOString() as ISO8601DateString;
}

/**
 * convert some string in arbitrary casing / spacing to PascalCase
 * https://stackoverflow.com/a/53952925
 */
export function toPascalCase(str: string) {
  if (/^[\p{L}\d]+$/iu.test(str)) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
  return str
    .replace(
      /([\p{L}\d])([\p{L}\d]*)/giu,
      (g0, g1, g2) => g1.toUpperCase() + g2.toLowerCase(),
    )
    .replace(/[^\p{L}\d]/giu, "");
}

export function toPascalCaseAndUriEncode(str: string) {
  return encodeURIComponent(toPascalCase(str));
}

export const generateYYYYMMRange = (
  start: YYYYMMDateString,
  end: YYYYMMDateString,
) => {
  const startDate = convertYYYYMMToDate(start);
  const endDate = convertYYYYMMToDate(end);
  const monthCount = differenceInMonths(endDate, startDate) + 1;

  return Array.from({ length: monthCount }, (_, index) => {
    return convertToYYYYMM(addMonths(startDate, index));
  });
};

export const createDateRange = ({
  startDate,
  endDate,
  length = 50,
  skipYears = 0,
}: {
  startDate: Date;
  endDate?: Date;
  length?: number;
  /** start X years after start Date */
  skipYears?: number;
}): Date[] => {
  const start = new Date(startDate);
  if (skipYears) {
    start.setFullYear(start.getFullYear() + skipYears);
  }

  const end = endDate || new Date(startDate);
  if (!endDate) {
    end.setFullYear(end.getFullYear() + length - 1); // -1 because length includes start year
  }

  const dates = [];
  const currentDate = new Date(start);

  while (currentDate <= end) {
    dates.push(new Date(currentDate));
    currentDate.setFullYear(currentDate.getFullYear() + 1);
  }

  return dates;
};

export function getEndOfFiscalYear(startDate: Date | YYYYMMDateString) {
  let startOfFiscalYear = startDate;
  if (typeof startOfFiscalYear === "string") {
    startOfFiscalYear = new Date(`${startDate}-01`);
  }
  return endOfMonth(addMonths(startOfFiscalYear, 11));
}

export function isCurrentFiscalYear(
  year: number,
  baseMonth: number,
  currentDate: Date = new Date(),
): boolean {
  // Get fiscal year start and end dates for the year being processed
  const fiscalYearStart = new Date(year, baseMonth);
  const fiscalYearEnd = new Date(year, baseMonth + 11);

  return (
    currentDate >= startOfMonth(fiscalYearStart) &&
    currentDate <= endOfMonth(fiscalYearEnd)
  );
}

export function getCurrentFiscalYear(fiscalYearStartMonth: number): number {
  const now = new Date();
  const currentMonth = now.getMonth() + 1; // JavaScript months are 0-based
  const currentYear = now.getFullYear();

  // If current month is before fiscal year start, subtract 1 from current year
  return currentMonth < fiscalYearStartMonth ? currentYear - 1 : currentYear;
}

export const isFiveFigures = (value: number) =>
  value.toString().split("").length >= 5;

export function isHexColour(value: string): value is HexColour {
  return /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value);
}

export function stripEmptyValues<T extends Record<string, any>>(
  obj: T,
): Partial<T> {
  const result: Record<string, any> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (value !== "" && value !== null && value !== undefined) {
      result[key] = value;
    }
  }

  return result as Partial<T>;
}
